import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { useApi } from './api-context'
import { SessionContext, type PortalSession } from './session'
import type { TokenStore } from './token-store'

/** Liga o token guardado no aparelho à sessão que as páginas leem (`useSession`). */
export function SessionProvider({ store, children }: { store: TokenStore; children: ReactNode }) {
  const api = useApi()
  const queryClient = useQueryClient()
  const token = useSyncExternalStore(store.subscribe, store.get, store.get)

  const signIn = useCallback(
    (next: string) => {
      queryClient.clear()
      store.set(next)
    },
    [queryClient, store],
  )

  const signOut = useCallback(async () => {
    // Encerrar na API é cortesia: o token some deste aparelho de qualquer forma.
    try {
      await api.post('/portal/sign-out')
    } catch {
      // A sessão já pode ter terminado; o resultado é o mesmo.
    }
    store.clear('signed_out')
    queryClient.clear()
  }, [api, queryClient, store])

  const value = useMemo<PortalSession>(
    () => ({
      status: token ? 'signed_in' : 'signed_out',
      reason: token ? null : store.reason(),
      signIn,
      signOut,
    }),
    [token, store, signIn, signOut],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
